import { NODI, ARCHI, COLORI, COL_ARCO } from './data/grafo';
import type { Nodo, Arco, TipoNodo } from './data/grafo';

export interface Filtri {
  corpus: string;                     // 'tutti' | 'fesr' | 'fse' | 'feasr'
  tipi: Record<TipoNodo, boolean>;
  abrogate: boolean;
}

interface Punto { x: number; y: number; vx: number; vy: number }

/**
 * Motore del grafo: simulazione a forze + disegno su canvas. Non conosce React.
 *
 * TRE COSE DA NON SEMPLIFICARE (ognuna nasce da un bug vero):
 *
 * 1. La misura del canvas è PIGRA, dentro il ciclo di disegno. Misurare una volta
 *    al mount produce un backing store 1x1 quando il contenitore non è ancora
 *    stato disposto, e non si recupera più.
 * 2. Il ciclo si RIAVVIA da sé: ogni frame registra un timestamp e una guardia
 *    esterna lo rimette in moto se il disegno si è fermato. Con viste montate e
 *    smontate (il canvas vive dentro un ramo condizionale) il requestAnimationFrame
 *    si interrompe senza errori in console.
 * 3. Il backing store va moltiplicato per devicePixelRatio, altrimenti su schermi
 *    non interi il grafo è sfocato o disallineato rispetto agli hit test.
 */
export class GrafoEngine {
  private cv: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private pos = new Map<string, Punto>();
  private filtri: Filtri;
  private sel: string | null = null;
  private hover: string | null = null;
  private scale = 1;
  private offset = { x: 0, y: 0 };
  private alpha = 1;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private raf: number | null = null;
  private ultimoFrame = 0;
  private guardia: number | null = null;
  private drag: { id: string; mosso: boolean } | null = null;
  private pan: { x: number; y: number } | null = null;

  onSelezione: (id: string | null) => void = () => {};
  onHover: (id: string | null) => void = () => {};

  constructor(canvas: HTMLCanvasElement, filtri: Filtri) {
    this.cv = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.filtri = filtri;
    this.disponi();
  }

  // ---- ciclo di vita ------------------------------------------------
  avvia() {
    this.cv.addEventListener('mousedown', this.giu);
    this.cv.addEventListener('mousemove', this.muovi);
    this.cv.addEventListener('wheel', this.rotella, { passive: false });
    this.cv.addEventListener('click', this.clic);
    window.addEventListener('mouseup', this.su);
    this.assicuraCiclo();
    this.guardia = window.setInterval(() => this.assicuraCiclo(), 700);
  }

  ferma() {
    if (this.guardia !== null) window.clearInterval(this.guardia);
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.cv.removeEventListener('mousedown', this.giu);
    this.cv.removeEventListener('mousemove', this.muovi);
    this.cv.removeEventListener('wheel', this.rotella);
    this.cv.removeEventListener('click', this.clic);
    window.removeEventListener('mouseup', this.su);
  }

  private assicuraCiclo() {
    if (this.raf === null) { this.raf = requestAnimationFrame(this.ciclo); return; }
    if (performance.now() - this.ultimoFrame < 400) return;   // vivo
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(this.ciclo);
  }

  // ---- API verso React ----------------------------------------------
  aggiornaFiltri(f: Filtri) { this.filtri = f; this.alpha = Math.max(this.alpha, 0.5); }
  seleziona(id: string | null) { this.sel = id; }
  zoom(fattore: number) { this.scale = Math.min(2.6, Math.max(0.4, this.scale * fattore)); }

  riassesta() {
    this.offset = { x: 0, y: 0 };
    this.scale = 1;
    this.disponi();
    this.alpha = 1;
  }

  private disponi() {
    NODI.forEach((n, i) => {
      const a = (i / NODI.length) * Math.PI * 2;
      this.pos.set(n.id, {
        x: Math.cos(a) * 190 + (i % 5) * 9,
        y: Math.sin(a) * 150 + (i % 3) * 11,
        vx: 0, vy: 0,
      });
    });
  }

  // ---- dati filtrati -------------------------------------------------
  private visibili(): Nodo[] {
    const { corpus, tipi, abrogate } = this.filtri;
    return NODI.filter((n) => {
      if (!tipi[n.tipo]) return false;
      if (!abrogate && !n.vigente) return false;
      if (corpus !== 'tutti' && !n.corpus.includes(corpus)) return false;
      return true;
    });
  }

  private archi(ids: Set<string>): Arco[] {
    return ARCHI.filter(([a, b]) => ids.has(a) && ids.has(b));
  }

  private raggio(n: Nodo) {
    if (n.tipo === 'corpus') return 19;
    if (n.tipo === 'atto') return 9;
    return 7 + Math.sqrt(n.cit) * 0.85;
  }

  // ---- interazione ---------------------------------------------------
  private mondo(ev: MouseEvent) {
    const r = this.cv.getBoundingClientRect();
    return {
      x: (ev.clientX - r.left - this.w / 2 - this.offset.x) / this.scale,
      y: (ev.clientY - r.top - this.h / 2 - this.offset.y) / this.scale,
    };
  }

  private sotto(ev: MouseEvent): Nodo | null {
    const p = this.mondo(ev);
    const vis = this.visibili();
    for (let i = vis.length - 1; i >= 0; i--) {
      const n = vis[i]!;
      const s = this.pos.get(n.id)!;
      const r = this.raggio(n) + 4;
      if ((p.x - s.x) ** 2 + (p.y - s.y) ** 2 <= r * r) return n;
    }
    return null;
  }

  private giu = (ev: MouseEvent) => {
    const n = this.sotto(ev);
    if (n) { this.drag = { id: n.id, mosso: false }; }
    else {
      const r = this.cv.getBoundingClientRect();
      this.pan = { x: ev.clientX - r.left - this.offset.x, y: ev.clientY - r.top - this.offset.y };
    }
    this.cv.style.cursor = 'grabbing';
  };

  private muovi = (ev: MouseEvent) => {
    if (this.drag) {
      const p = this.mondo(ev);
      const s = this.pos.get(this.drag.id)!;
      s.x = p.x; s.y = p.y; s.vx = 0; s.vy = 0;
      this.drag.mosso = true;
      this.alpha = Math.max(this.alpha, 0.35);
      return;
    }
    if (this.pan) {
      const r = this.cv.getBoundingClientRect();
      this.offset.x = ev.clientX - r.left - this.pan.x;
      this.offset.y = ev.clientY - r.top - this.pan.y;
      return;
    }
    const n = this.sotto(ev);
    const id = n ? n.id : null;
    if (id !== this.hover) { this.hover = id; this.onHover(id); }
    this.cv.style.cursor = n ? 'pointer' : 'grab';
  };

  private su = () => { this.drag = null; this.pan = null; this.cv.style.cursor = 'grab'; };

  private clic = (ev: MouseEvent) => {
    if (this.drag && this.drag.mosso) return;
    const n = this.sotto(ev);
    this.sel = n ? n.id : null;
    this.onSelezione(this.sel);
  };

  private rotella = (ev: WheelEvent) => {
    ev.preventDefault();
    this.zoom(ev.deltaY < 0 ? 1.12 : 1 / 1.12);
  };

  // ---- simulazione e disegno -----------------------------------------
  private ciclo = () => {
    this.ultimoFrame = performance.now();
    this.raf = requestAnimationFrame(this.ciclo);

    const cw = this.cv.clientWidth, ch = this.cv.clientHeight;
    if (!cw || !ch) return;                       // layout non ancora assestato
    if (cw !== this.w || ch !== this.h) {
      this.dpr = window.devicePixelRatio || 1;
      this.cv.width = Math.round(cw * this.dpr);
      this.cv.height = Math.round(ch * this.dpr);
      this.w = cw; this.h = ch;
      this.alpha = Math.max(this.alpha, 0.4);
    }

    const vis = this.visibili();
    const ids = new Set(vis.map((n) => n.id));
    const archi = this.archi(ids);

    if (this.alpha > 0.005) {
      for (let i = 0; i < vis.length; i++) {
        const a = this.pos.get(vis[i]!.id)!;
        for (let j = i + 1; j < vis.length; j++) {
          const b = this.pos.get(vis[j]!.id)!;
          let dx = b.x - a.x, dy = b.y - a.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) { d2 = 1; dx = Math.random() - 0.5; dy = Math.random() - 0.5; }
          const d = Math.sqrt(d2);
          const rep = 5200 / d2;
          const fx = (dx / d) * rep, fy = (dy / d) * rep;
          a.vx -= fx; a.vy -= fy; b.vx += fx; b.vy += fy;
        }
      }
      for (const [ia, ib, rel] of archi) {
        const a = this.pos.get(ia)!, b = this.pos.get(ib)!;
        const dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.max(1, Math.hypot(dx, dy));
        const target = rel === 'contiene' ? 132 : rel === 'abroga' ? 74 : 100;
        const k = (d - target) * 0.0125;
        const fx = (dx / d) * k, fy = (dy / d) * k;
        a.vx += fx; a.vy += fy; b.vx -= fx; b.vy -= fy;
      }
      for (const n of vis) {
        const s = this.pos.get(n.id)!;
        s.vx -= s.x * 0.0034;
        s.vy -= s.y * 0.0034;
        s.vx *= 0.82; s.vy *= 0.82;
        if (!this.drag || this.drag.id !== n.id) { s.x += s.vx * this.alpha; s.y += s.vy * this.alpha; }
      }
      this.alpha *= 0.988;
    }

    this.disegna(vis, archi);
  };

  private disegna(vis: Nodo[], archi: Arco[]) {
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.save();
    ctx.translate(this.w / 2 + this.offset.x, this.h / 2 + this.offset.y);
    ctx.scale(this.scale, this.scale);

    const focus = this.hover || this.sel;
    const vicini = new Set<string>();
    if (focus) {
      vicini.add(focus);
      for (const [a, b] of archi) {
        if (a === focus) vicini.add(b);
        if (b === focus) vicini.add(a);
      }
    }

    for (const [ia, ib, rel] of archi) {
      const a = this.pos.get(ia)!, b = this.pos.get(ib)!;
      const acceso = !!focus && (ia === focus || ib === focus);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = COL_ARCO[rel];
      ctx.globalAlpha = focus ? (acceso ? 0.95 : 0.07) : rel === 'cita' ? 0.4 : 0.55;
      ctx.lineWidth = acceso ? 2 : rel === 'contiene' ? 1.1 : 1;
      ctx.setLineDash(rel === 'abroga' ? [5, 4] : []);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.globalAlpha = 1;

    for (const n of vis) {
      const s = this.pos.get(n.id)!;
      const r = this.raggio(n);
      const spento = !!focus && !vicini.has(n.id);
      ctx.globalAlpha = spento ? 0.14 : 1;

      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      // Norma abrogata = cerchio VUOTO tratteggiato: presente nel corpus,
      // visibilmente non citabile come base giuridica.
      ctx.fillStyle = n.vigente ? COLORI[n.tipo] : '#fcfbf9';
      ctx.fill();
      if (!n.vigente) {
        ctx.strokeStyle = COLORI[n.tipo];
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (n.id === this.sel) {
        ctx.beginPath();
        ctx.arc(s.x, s.y, r + 5, 0, Math.PI * 2);
        ctx.strokeStyle = '#1b2230';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      const etichetta = !spento && (r >= 11 || n.id === focus || (!!focus && vicini.has(n.id)) || this.scale > 1.25);
      if (etichetta) {
        ctx.font = (n.tipo === 'corpus' ? '600 13px ' : '400 12px ') + '"Titillium Web", system-ui, sans-serif';
        const tw = ctx.measureText(n.label).width;
        const ty = s.y + r + 14;
        ctx.globalAlpha = spento ? 0.14 : 0.92;
        ctx.fillStyle = 'rgba(252,251,249,.86)';
        ctx.fillRect(s.x - tw / 2 - 3, ty - 11, tw + 6, 15);
        ctx.fillStyle = '#1b2230';
        ctx.textAlign = 'center';
        ctx.fillText(n.label, s.x, ty);
      }
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }
}
